import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useState } from "react";
import { PageHeader, fmt } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ReceiptText,
  Search,
  Banknote,
  Smartphone,
  CreditCard,
  Printer,
  ChevronDown,
  ChevronUp,
  Calendar,
  Clock,
  User,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import { hasRole, useAuth } from "@/lib/auth-store";
import { tabStore, useTabStore, type Tab, type TabItem } from "@/lib/tab-store";

export const Route = createFileRoute("/tabs-history")({
  beforeLoad: ({ context }) => {
    if (!hasRole(context.user, "waiter")) {
      throw redirect({ to: "/" });
    }
  },
  head: () => ({
    meta: [
      { title: "Closed Tabs — History" },
      {
        name: "description",
        content: "Browse closed tabs and re-print receipts.",
      },
    ],
  }),
  component: TabsHistoryPage,
});

function TabsHistoryPage() {
  const { user } = useAuth();
  const allTabs = useTabStore((s) => s.tabs);
  const [searchQ, setSearchQ] = useState("");
  const [detailTab, setDetailTab] = useState<Tab | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Closed tabs, newest first
  const closedTabs = allTabs
    .filter((t) => t.status === "closed")
    .sort((a, b) => b.createdAt - a.createdAt);

  // If user is a waiter, only show their own tabs
  const isStaffWaiter = user?.role === "waiter";
  const filtered = closedTabs
    .filter((t) => (isStaffWaiter ? t.waiterId === user?.id : true))
    .filter(
      (t) =>
        t.customerName.toLowerCase().includes(searchQ.toLowerCase()) ||
        t.id.toLowerCase().includes(searchQ.toLowerCase()),
    );

  const handlePrintReceipt = (tab: Tab) => {
    const printWin = window.open("", "_blank", "width=400,height=600");
    if (!printWin) return;

    const total = tab.items.reduce((a, i) => a + i.total, 0);
    const paid = tab.payments.reduce((a, p) => a + p.amount, 0);
    const balance = total - paid;

    const itemsHtml = tab.items
      .map(
        (item) => `
        <tr>
          <td style="padding:2px 0;">
            ${item.name}
            <br><span style="color:#888;font-size:10px;">${item.qty} × ${fmt(item.unitPrice)}</span>
          </td>
          <td style="text-align:right;padding:2px 0;white-space:nowrap;">${fmt(item.total)}</td>
        </tr>`,
      )
      .join("");

    const paymentsHtml = tab.payments
      .map(
        (p) => `
        <tr>
          <td style="padding:2px 0;text-transform:capitalize">${p.method}</td>
          <td style="text-align:right;padding:2px 0;white-space:nowrap;">${fmt(p.amount)}</td>
        </tr>`,
      )
      .join("");

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Tab Receipt</title>
  <style>
    @page { margin: 0; }
    body {
      font-family: 'Courier New', Courier, monospace;
      font-size: 12px;
      margin: 0;
      padding: 32px 16px 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
      background: white;
      color: black;
    }
    .receipt-body { width: 80mm; max-width: 100%; }
    .center { text-align: center; }
    .bold { font-weight: bold; }
    .line { border-top: 1px dashed #999; margin: 8px 0; }
    table { width: 100%; border-collapse: collapse; }
    td:last-child { text-align: right; white-space: nowrap; }
    .total td { padding-top: 4px; font-size: 14px; font-weight: bold; }
  </style>
</head>
<body>
  <div class="receipt-body">
    <div class="center bold" style="font-size:14px;">White House Lounge</div>
    <div class="center" style="color:#888;font-size:10px;">TAB CLOSED · #${tab.id.slice(-6).toUpperCase()}</div>
    <div class="line"></div>
    <div>Customer: <strong>${tab.customerName}</strong></div>
    <div>Date: ${new Date(Number(tab.createdAt)).toLocaleString()}</div>
    ${tab.waiterId ? `<div>Served by: ${user?.displayName ?? "Waiter"}</div>` : ""}
    <div class="line"></div>
    <table>
      <tr class="bold"><td>Item</td><td>Total</td></tr>
      ${itemsHtml}
    </table>
    <div class="line"></div>
    <table>
      <tr class="bold"><td>Payments</td><td></td></tr>
      ${paymentsHtml}
    </table>
    <div class="line"></div>
    <table>
      <tr><td>Total Ordered</td><td>${fmt(total)}</td></tr>
      <tr><td>Total Paid</td><td>${fmt(paid)}</td></tr>
      <tr class="total"><td>${balance <= 0 ? "SETTLED" : "BALANCE"}</td><td>${fmt(Math.abs(balance))}</td></tr>
    </table>
    <div class="center" style="color:#888;font-size:10px;margin-top:16px;">Thank you · Karibu tena</div>
  </div>
</body>
</html>`;

    printWin.document.write(html);
    printWin.document.close();
    printWin.focus();
    printWin.print();
  };

  return (
    <div>
      <PageHeader
        title="Closed Tabs"
        subtitle={`${filtered.length} tab${filtered.length !== 1 ? "s" : ""}`}
      />

      {/* Search */}
      <div className="px-4 pt-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search customer name..."
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
          />
        </div>
      </div>

      {/* Closed Tabs List */}
      <div className="space-y-2 px-4 pt-4 pb-24">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center pt-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent">
              <ReceiptText className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="mt-4 font-display text-lg font-semibold">
              No Closed Tabs
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {searchQ
                ? "No tabs match your search"
                : "Closed tabs will appear here"}
            </p>
          </div>
        ) : (
          filtered.map((tab) => {
            const total = tab.items.reduce((a, i) => a + i.total, 0);
            const paid = tab.payments.reduce((a, p) => a + p.amount, 0);
            const isExpanded = expandedId === tab.id;

            return (
              <div
                key={tab.id}
                className="rounded-xl border border-border bg-card overflow-hidden"
              >
                {/* Summary row */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : tab.id)}
                  className="w-full p-3 text-left active:scale-[0.98] min-h-[56px]"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-accent-foreground font-display text-sm font-bold">
                      {tab.customerName.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{tab.customerName}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(Number(tab.createdAt)).toLocaleDateString(
                          "en-KE",
                          {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          },
                        )}
                        <Clock className="h-3 w-3 ml-1" />
                        {new Date(Number(tab.createdAt)).toLocaleTimeString(
                          [],
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-sm font-semibold">
                        {fmt(paid)}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {tab.items.length} item
                        {tab.items.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t border-border px-3 py-3 space-y-3">
                    {/* Items */}
                    <div>
                      <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                        Items Ordered
                      </h4>
                      <div className="divide-y divide-border rounded-lg border border-border">
                        {tab.items.map((item, idx) => (
                          <div
                            key={item.itemId + idx}
                            className="flex items-center gap-2 px-2 py-1.5"
                          >
                            <span className="flex h-6 w-6 items-center justify-center rounded bg-accent text-[10px] font-bold text-accent-foreground">
                              {item.qty}
                            </span>
                            <p className="flex-1 text-xs">{item.name}</p>
                            <p className="text-xs font-semibold">
                              {fmt(item.total)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Payments */}
                    {tab.payments.length > 0 && (
                      <div>
                        <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                          Payments
                        </h4>
                        <div className="divide-y divide-border rounded-lg border border-border">
                          {tab.payments.map((p) => (
                            <div
                              key={p.id}
                              className="flex items-center gap-2 px-2 py-1.5"
                            >
                              {p.method === "cash" ? (
                                <Banknote className="h-3.5 w-3.5 text-muted-foreground" />
                              ) : p.method === "mpesa" ? (
                                <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />
                              ) : (
                                <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
                              )}
                              <p className="flex-1 text-xs capitalize">
                                {p.method}
                              </p>
                              <p className="text-xs font-semibold text-primary">
                                {fmt(p.amount)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Summary & actions */}
                    <div className="flex items-center justify-between pt-1">
                      <div className="text-xs text-muted-foreground">
                        <span>Total: {fmt(total)}</span>
                        <span className="ml-2">Paid: {fmt(paid)}</span>
                        {paid >= total && (
                          <span className="ml-2 text-primary font-semibold">
                            ✓ Settled
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs gap-1"
                          onClick={() => handlePrintReceipt(tab)}
                        >
                          <Printer className="h-3.5 w-3.5" />
                          Receipt
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
